'use client';

import { LDProvider } from 'launchdarkly-react-client-sdk';
import { type PropsWithChildren } from 'react';

import { flagsContext } from '@/contexts/flagsContext';

import { useEnvironment } from './useEnvironment';
import { useUser } from './useUser';

export const LaunchDarklyProvider = (props: PropsWithChildren) => {
  const { isDev, isDemo, isTest, launchDarklyClientId } = useEnvironment();
  const { currentProjectId, user } = useUser();
  if (!currentProjectId) {
    return props.children;
  }

  if (isDemo || isDev || isTest) {
    return (
      <flagsContext.Provider
        value={{
          flags: {
            // Sivan
            AllowAccountLevelSamlWhenOrgAttached: false,

            // Amir
            NonCognitoEonAccounts: false,
            SkipOnboarding: false,
            dlsgFrontend: false,
            LlgFrontend: false,
            NewFilterBar: isDev,
            // LakehouseBaseUrl: 'http://localhost:3003',

            // Erez
            JobsStepsEnabled: true,
            enableHourlyBackup: false, // opened only for Fanatics
            AzureSqlVirtualMachineSupport: true, // permanent
            ownCustomerOnboardingEnabled: false,
            byovEnabled: true,
            hourlyDlsgPolicyEnabled: false,
            OnboardTenantAgainEnabled: true,
            GcpRestoreOrgAndFolderSupport: true,
            SecurityFindings: true,
            ClassificationPoliciesEnabled: true,

            // Eugene
            Ec2HighFrequencyExcludePlatformEncryption: true,

            // Yaara
            EnableGDSGPage: isDev,
            EnableGdsgNotifications: false,
            StorageGatewayCostExplorer: false,
            EnableGdsgObjectBrowser: isDev,
            EnableGdsgObjectActions: isDev,

            // Moshe
            SapHanaPitrSupport: true,
            MongoAtlasSupport: true,

            // Ilan - Mongo Atlas Restore
            MongoAtlasAdvancedRestoreEnabled: isDev,

            // Ilya
            ExploreFilesForDatabaseVms: true,

            // Keren
            RdsRestoreWithoutEncryptionEnabled: false,
            RecordRestoreResultsEnabled: true,

            // Dvir
            GcpVmHighFrequencyBackupEnabled: true,
            BypassSourceAccountRequirement: false,

            // Abir
            RdsHighFrequencyBackupEnabled: true,

            // Eldar
            // false matches the backend's LocalValue; true would block every
            // cross-cloud Azure-vault eligibility path (including the DB flags below)
            EnableAzureVaultNetworkIsolation: false,
            // Nitzan
            EnableCrossCloudDbBackup: true,
            EnableCrossCloudDbBackupToAzureVault: true,
            EnableCrossCloudDynamoKeyspacesBackup: true,

            // Ariel
            AzureSnapshotsDiscoverySupport: true,

            // Ran
            AwsSelectiveOnboarding: true,
            SkipOrgUnitInvokeDiscovery: true,
            AzureFilesSupport: true,
            AwsRdsNativeBackupSupport: true,
            // Tzachi
            AzureMultipleAppRegistrations: false,
            AzureCustomPermissionsModel: false,

            // Google Workspace
            GoogleWorkspaceSupport: true,
            GwsInPlaceRestoreOnly: false,
            GwsRestoreToTargetMailbox: true,
            GwsRestoreToTargetDrive: true,
            GwsRestoreWholeMailbox: true,

            // Microsoft 365
            Microsoft365Support: true,
            Microsoft365RestoreSupport: true,

            // Eden
            EnableMrapForByov: true,

            // Oryan
            BigQueryNativeRestoreEnabled: true,
            EnableBigQueryNativePolicy: true,
            GcpProjectLabelTagFilterEnabled: true,
            CancelBackupJobEnabled: true,
            BigQueryRestoreOverwriteEnabled: true,

            // Benji
            SensitivityAnnotations: true,
            EnableNetworkClassification: true,
            RestoreObfuscationEnabled: false,

            // Liran - RDS Restore
            RdsRestorePreserveOriginalMemberNames: false,
            AwsKeyspacesSupport: true,
            CassandraSupport: true,

            // MatanR
            RdsCdcUiEnabled: true,

            // Or
            AiAgents: isDev,

            // Yair
            ZeroEtlUiEnabled: true,
            ZeroEtlUiJobsTabEnabled: true,
            LakehouseEnabled: false,
            lakehouseZeroEtlUiEnabled: true,
            ZeroEtlDynamoDbEnabled: false,
            ZeroEtlMongoDbEnabled: true,
            LakehouseDataQualityUiEnabled: true,
            ZeroEtlMultiResourcePolicies: false,
            DataKnowledgeGraphEnabled: false,
            AgenticDataLayerEnabled: false,

            // Daniel - Export access-denied items
            EnableExportAccessDeniedItems: true,
            InventoryIssuesColumn: false,

            // Lior
            RestoreTemplates: true,
            BulkRestore: true,
            ThreatHunting: true,
            RemediationOrchestration: true,

            // Jonathan - Inventory entities filter
            EnableInventoryEntitiesFilter: true,

            // Zohar - SaaS inventory default landing page
            SaasInventoryDefaultLanding: false,

            // Ilan K
            BackupComplianceJson: {
              view: true,
              trend: true,
              export: true,
              audit: false,
              inventoryFilters: true,
            },
            LakehouseSAPConnectorJson: {
              wizard: true,
              management: true,
              purge: false,
              backend: false,
            },

            // Setter
            EnableInstantRestore: true,
            EnableInstantVolumeRestore: true,
            UseTargetVpcForRestoreServer: true,
            UseTargetVpcForFileRestoreServer: true,

            // Yuval
            ClassificationOnlyCostExplorer: true,
          },
          flagKeyMap: {},
          ldClient: undefined,
        }}
      >
        {props.children}
      </flagsContext.Provider>
    );
  }

  if (!launchDarklyClientId || !user?.eonAccount) {
    return props.children;
  }

  return (
    <LDProvider
      reactOptions={{
        reactContext: flagsContext,
        useCamelCaseFlagKeys: false,
      }}
      clientSideID={launchDarklyClientId}
      timeout={2}
      context={{
        kind: 'multi',
        user: {
          key: currentProjectId, // backward compatibility
          // name: user.givenName,
          // email: user.email,
          // id: user.id,
          name: user.eonAccount.name,
          domain: user.eonAccount.domain,
        },
        organization: {
          key: user.eonAccount.id,
          name: user.eonAccount.name,
          domain: user.eonAccount.domain,
          projectId: currentProjectId,
        },
      }}
      options={{ streaming: false }}
    >
      {props.children}
    </LDProvider>
  );
};
